/**
 * PinLockScreen — Full-screen PIN entry overlay.
 *
 * Covers the entire messenger when PIN lock is active.
 * Features:
 * - Numeric keypad for PIN entry
 * - Failed attempt counter with lockout
 * - Shake animation on wrong PIN
 * - Auto-lock on tab visibility change
 */

import { useState, useCallback, useEffect, useRef } from "react";
import { Lock, Delete, AlertCircle } from "lucide-react";
import { cn } from "@slumbr/ui";

import { useMessengerStore } from "../stores/messenger-store";

// ─── PIN Dot Display ─────────────────────────────────────────────────────────

function PinDots({ length, maxLength }: { length: number; maxLength: number }) {
  return (
    <div className="flex items-center justify-center gap-3">
      {Array.from({ length: maxLength }).map((_, i) => (
        <div
          key={i}
          className={cn(
            "h-3.5 w-3.5 rounded-full transition-all duration-150",
            i < length
              ? "scale-110 bg-brand-500"
              : "border-2 border-gray-300 dark:border-gray-600"
          )}
        />
      ))}
    </div>
  );
}

// ─── Numeric Keypad ──────────────────────────────────────────────────────────

function NumericKeypad({
  onDigit,
  onDelete,
  onSubmit,
  disabled,
}: {
  onDigit: (digit: string) => void;
  onDelete: () => void;
  onSubmit: () => void;
  disabled: boolean;
}) {
  const keys = [
    ["1", "2", "3"],
    ["4", "5", "6"],
    ["7", "8", "9"],
    ["", "0", "del"],
  ];

  return (
    <div className="grid grid-cols-3 gap-3">
      {keys.flat().map((key, i) => {
        if (key === "") return <div key={i} />;
        if (key === "del") {
          return (
            <button
              key={i}
              onClick={onDelete}
              disabled={disabled}
              className="flex h-14 w-14 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-surface-2 active:bg-surface-3 disabled:opacity-50 dark:text-gray-400 dark:hover:bg-surface-dark-2 dark:active:bg-surface-dark-3"
            >
              <Delete className="h-5 w-5" />
            </button>
          );
        }
        return (
          <button
            key={i}
            onClick={() => onDigit(key)}
            disabled={disabled}
            className="flex h-14 w-14 items-center justify-center rounded-full text-lg font-semibold text-gray-800 transition-colors hover:bg-surface-2 active:bg-surface-3 disabled:opacity-50 dark:text-gray-200 dark:hover:bg-surface-dark-2 dark:active:bg-surface-dark-3"
          >
            {key}
          </button>
        );
      })}
    </div>
  );
}

// ─── Lock Screen ─────────────────────────────────────────────────────────────

export function PinLockScreen() {
  const isLocked = useMessengerStore((s) => s.isMessengerLocked);
  const unlockMessenger = useMessengerStore((s) => s.unlockMessenger);
  const lockMessenger = useMessengerStore((s) => s.lockMessenger);
  const encryptionSettings = useMessengerStore((s) => s.encryptionSettings);

  const [pin, setPin] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [shake, setShake] = useState(false);
  const [checking, setChecking] = useState(false);
  const maxPinLength = 8;
  const containerRef = useRef<HTMLDivElement>(null);

  const { pinLock } = encryptionSettings;

  // Auto-lock on tab visibility change
  useEffect(() => {
    if (!pinLock.isEnabled || !pinLock.lockOnTabHidden) return;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        lockMessenger();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [pinLock.isEnabled, pinLock.lockOnTabHidden, lockMessenger]);

  // Auto-lock after timeout
  useEffect(() => {
    if (!pinLock.isEnabled || isLocked || pinLock.autoLockTimeout === 0) return;

    let timer: ReturnType<typeof setTimeout>;

    const resetTimer = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        lockMessenger();
      }, pinLock.autoLockTimeout);
    };

    // Reset on user activity
    const events = ["mousedown", "keydown", "touchstart", "scroll"];
    events.forEach((e) => window.addEventListener(e, resetTimer, { passive: true }));
    resetTimer();

    return () => {
      clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, resetTimer));
    };
  }, [pinLock.isEnabled, pinLock.autoLockTimeout, isLocked, lockMessenger]);

  const handleDigit = useCallback(
    (digit: string) => {
      if (pin.length >= maxPinLength) return;
      setError(null);
      setPin((p) => p + digit);
    },
    [pin.length]
  );

  const handleDelete = useCallback(() => {
    setPin((p) => p.slice(0, -1));
    setError(null);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (pin.length < 4) return;
    setChecking(true);
    const success = await unlockMessenger(pin);
    setChecking(false);

    if (!success) {
      setShake(true);
      setError(
        pinLock.lockoutUntil
          ? "Too many attempts. Try again later."
          : "Incorrect PIN"
      );
      setPin("");
      setTimeout(() => setShake(false), 500);
    }
  }, [pin, unlockMessenger, pinLock.lockoutUntil]);

  // Auto-submit when PIN reaches max length or common PIN lengths
  useEffect(() => {
    if (pin.length >= 4 && pin.length <= maxPinLength) {
      // Small delay so the last dot animates before checking
      const timer = setTimeout(() => {
        if (pin.length === maxPinLength) handleSubmit();
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [pin.length, handleSubmit]);

  // Keyboard support
  useEffect(() => {
    if (!isLocked) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key >= "0" && e.key <= "9") {
        handleDigit(e.key);
      } else if (e.key === "Backspace") {
        handleDelete();
      } else if (e.key === "Enter") {
        handleSubmit();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isLocked, handleDigit, handleDelete, handleSubmit]);

  // Check lockout status
  const isLockedOut = !!(
    pinLock.lockoutUntil &&
    new Date(pinLock.lockoutUntil).getTime() > Date.now()
  );

  if (!isLocked) return null;

  return (
    <div className="absolute inset-0 z-50 flex items-center justify-center bg-surface-0/95 backdrop-blur-lg dark:bg-surface-dark-0/95">
      <div
        ref={containerRef}
        className={cn(
          "flex flex-col items-center gap-8",
          shake && "animate-[shake_0.5s_ease-in-out]"
        )}
      >
        {/* Lock icon */}
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-brand-500/10">
          <Lock className="h-7 w-7 text-brand-500" />
        </div>

        {/* Title */}
        <div className="text-center">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
            Messenger Locked
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Enter your PIN to unlock
          </p>
        </div>

        {/* PIN dots */}
        <PinDots length={pin.length} maxLength={maxPinLength} />

        {/* Error message */}
        {error && (
          <div className="flex items-center gap-1.5 text-sm text-red-500">
            <AlertCircle className="h-4 w-4" />
            {error}
          </div>
        )}

        {/* Lockout info */}
        {isLockedOut && pinLock.lockoutUntil && (
          <p className="text-xs text-red-500">
            Locked until{" "}
            {new Date(pinLock.lockoutUntil).toLocaleTimeString()}
          </p>
        )}

        {/* Keypad */}
        <NumericKeypad
          onDigit={handleDigit}
          onDelete={handleDelete}
          onSubmit={handleSubmit}
          disabled={checking || isLockedOut}
        />

        {/* Submit for shorter PINs */}
        {pin.length >= 4 && pin.length < maxPinLength && (
          <button
            onClick={handleSubmit}
            disabled={checking || isLockedOut}
            className="rounded-lg bg-brand-500 px-6 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-600 disabled:opacity-50"
          >
            {checking ? "Checking..." : "Unlock"}
          </button>
        )}

        {/* Failed attempts */}
        {pinLock.failedAttempts > 0 && (
          <p className="text-[11px] text-gray-400">
            {pinLock.failedAttempts} failed attempt
            {pinLock.failedAttempts !== 1 ? "s" : ""}
            {pinLock.maxAttempts > 0 && ` of ${pinLock.maxAttempts}`}
          </p>
        )}
      </div>

      {/* CSS keyframe for shake animation */}
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-8px); }
          20%, 40%, 60%, 80% { transform: translateX(8px); }
        }
      `}</style>
    </div>
  );
}
